import { useState } from 'react';
import { useTheme } from '../../hooks/useTheme';
import { ColorPicker } from '../ui/ColorPicker';
import type { ColorPalette, ColorShade } from '../../types/theme';

interface ThemeConfigPanelProps {
  onClose?: () => void;
}

export function ThemeConfigPanel({ onClose }: ThemeConfigPanelProps) {
  const { theme, updateTheme, resetTheme, applyPreset } = useTheme();
  const [activeTab, setActiveTab] = useState<'colors' | 'typography' | 'spacing'>('colors');

  const handleColorChange = (
    colorType: keyof typeof theme.colors,
    shade: ColorShade | string,
    value: string
  ) => {
    if (
      colorType === 'success' ||
      colorType === 'warning' ||
      colorType === 'error' ||
      colorType === 'info'
    ) {
      updateTheme({
        colors: {
          ...theme.colors,
          [colorType]: value,
        },
      });
    } else {
      updateTheme({
        colors: {
          ...theme.colors,
          [colorType]: {
            ...(theme.colors[colorType] as ColorPalette),
            [shade]: value,
          },
        },
      });
    }
  };

  const handleTypographyChange = (property: string, value: string | number) => {
    const [category, key] = property.split('.');
    updateTheme({
      typography: {
        ...theme.typography,
        [category]: {
          ...theme.typography[category as keyof typeof theme.typography],
          [key]: value,
        },
      },
    });
  };

  const handleSpacingChange = (key: keyof typeof theme.spacing, value: string) => {
    updateTheme({
      spacing: {
        ...theme.spacing,
        [key]: value,
      },
    });
  };

  const tabs = [
    {
      id: 'colors' as const,
      label: 'Colors',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"
          />
        </svg>
      ),
    },
    {
      id: 'typography' as const,
      label: 'Typography',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 6h16M4 12h8m-8 6h16"
          />
        </svg>
      ),
    },
    {
      id: 'spacing' as const,
      label: 'Spacing',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"
          />
        </svg>
      ),
    },
  ];

  const presets = [
    { id: 'default', name: 'Default', colors: ['#3b82f6', '#64748b', '#d946ef'] },
    { id: 'dark', name: 'Dark', colors: ['#60a5fa', '#9ca3af', '#e879f9'] },
    { id: 'ocean', name: 'Ocean', colors: ['#0ea5e9', '#06b6d4', '#38bdf8'] },
    { id: 'forest', name: 'Forest', colors: ['#22c55e', '#84cc16', '#4ade80'] },
    { id: 'sunset', name: 'Sunset', colors: ['#f97316', '#ef4444', '#d946ef'] },
  ];

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/50">
        <h2 className="text-lg font-semibold text-gray-900">Theme Configuration</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={resetTheme}
            className="px-3 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-all"
          >
            Reset
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Presets */}
      <div className="px-6 py-4 border-b border-gray-100">
        <h3 className="text-sm font-medium text-gray-700 mb-3">Quick Presets</h3>
        <div className="flex flex-wrap gap-2">
          {presets.map((preset) => (
            <button
              key={preset.id}
              onClick={() => applyPreset(preset.id)}
              className="group flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-all"
            >
              <div className="flex -space-x-1">
                {preset.colors.map((color, index) => (
                  <div
                    key={index}
                    className="w-4 h-4 rounded-full border-2 border-white shadow-sm"
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
              <span className="text-sm font-medium text-gray-700 group-hover:text-primary-700">
                {preset.name}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-100">
        <nav className="flex px-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors
                ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }
              `}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="p-6 max-h-[500px] overflow-y-auto">
        {activeTab === 'colors' && (
          <div className="space-y-8">
            {/* Primary Colors */}
            <section>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-3 h-3 rounded-full bg-primary-500" />
                <h4 className="text-sm font-semibold text-gray-900">Primary Colors</h4>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {Object.entries(theme.colors.primary).map(([shade, color]) => (
                  <ColorPicker
                    key={`primary-${shade}`}
                    label={shade}
                    value={color}
                    onChange={(value) => handleColorChange('primary', shade as ColorShade, value)}
                    className="text-xs"
                  />
                ))}
              </div>
            </section>

            {/* Secondary Colors */}
            <section>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-3 h-3 rounded-full bg-gray-500" />
                <h4 className="text-sm font-semibold text-gray-900">Secondary Colors</h4>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {Object.entries(theme.colors.secondary).map(([shade, color]) => (
                  <ColorPicker
                    key={`secondary-${shade}`}
                    label={shade}
                    value={color}
                    onChange={(value) => handleColorChange('secondary', shade as ColorShade, value)}
                    className="text-xs"
                  />
                ))}
              </div>
            </section>

            {/* Semantic Colors */}
            <section>
              <div className="flex items-center gap-2 mb-4">
                <div className="flex -space-x-1">
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500" />
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                </div>
                <h4 className="text-sm font-semibold text-gray-900">Semantic Colors</h4>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {(['success', 'warning', 'error', 'info'] as const).map((colorType) => (
                  <ColorPicker
                    key={colorType}
                    label={colorType.charAt(0).toUpperCase() + colorType.slice(1)}
                    value={theme.colors[colorType]}
                    onChange={(value) => handleColorChange(colorType, '', value)}
                  />
                ))}
              </div>
            </section>
          </div>
        )}

        {activeTab === 'typography' && (
          <div className="space-y-8">
            {/* Font Families */}
            <section>
              <h4 className="text-sm font-semibold text-gray-900 mb-4">Font Families</h4>
              <div className="space-y-4">
                {Object.entries(theme.typography.fontFamily).map(([key, value]) => (
                  <div key={key}>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5">
                      {key}
                    </label>
                    <input
                      type="text"
                      value={value}
                      onChange={(e) => handleTypographyChange(`fontFamily.${key}`, e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all"
                    />
                  </div>
                ))}
              </div>
            </section>

            {/* Font Sizes */}
            <section>
              <h4 className="text-sm font-semibold text-gray-900 mb-4">Font Sizes</h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {Object.entries(theme.typography.fontSize).map(([key, value]) => (
                  <div key={key}>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5">
                      {key}
                    </label>
                    <input
                      type="text"
                      value={value}
                      onChange={(e) => handleTypographyChange(`fontSize.${key}`, e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all"
                      placeholder="1rem"
                    />
                  </div>
                ))}
              </div>
            </section>

            {/* Font Weights */}
            <section>
              <h4 className="text-sm font-semibold text-gray-900 mb-4">Font Weights</h4>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {Object.entries(theme.typography.fontWeight).map(([key, value]) => (
                  <div key={key}>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5">
                      {key}
                    </label>
                    <input
                      type="number"
                      value={value}
                      onChange={(e) =>
                        handleTypographyChange(`fontWeight.${key}`, parseInt(e.target.value))
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all"
                      min="100"
                      max="900"
                      step="100"
                    />
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}

        {activeTab === 'spacing' && (
          <div className="space-y-8">
            {/* Spacing Scale */}
            <section>
              <h4 className="text-sm font-semibold text-gray-900 mb-4">Spacing Scale</h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {Object.entries(theme.spacing).map(([key, value]) => (
                  <div key={key}>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5">
                      {key}
                    </label>
                    <input
                      type="text"
                      value={value}
                      onChange={(e) =>
                        handleSpacingChange(key as keyof typeof theme.spacing, e.target.value)
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all"
                      placeholder="1rem"
                    />
                    <div
                      className="mt-2 h-2 bg-gradient-to-r from-primary-400 to-primary-600 rounded-full"
                      style={{ width: value }}
                    />
                  </div>
                ))}
              </div>
            </section>

            {/* Border Radius */}
            <section>
              <h4 className="text-sm font-semibold text-gray-900 mb-4">Border Radius</h4>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {Object.entries(theme.borderRadius).map(([key, value]) => (
                  <div key={key}>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5">
                      {key}
                    </label>
                    <input
                      type="text"
                      value={value}
                      onChange={(e) =>
                        updateTheme({
                          borderRadius: {
                            ...theme.borderRadius,
                            [key]: e.target.value,
                          },
                        })
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all"
                      placeholder="0.25rem"
                    />
                    <div
                      className="mt-2 w-10 h-10 bg-gradient-to-br from-primary-400 to-primary-600"
                      style={{ borderRadius: value }}
                    />
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
